/**
 * Parses markdown content into sections based on ## (h2) headers.
 * Used for blog posts with sectioned navigation.
 */
export interface BlogSection {
  title: string;
  content: string;
}

export function parseMarkdownSections(content: string): BlogSection[] {
  if (!content || !content.trim()) {
    return [{ title: 'Article', content: '' }];
  }

  const sections: BlogSection[] = [];
  const lines = content.split('\n');
  let currentTitle = 'Overview';
  let currentContent: string[] = [];
  let isFirstSection = true;

  for (const line of lines) {
    const h2Match = line.match(/^## (.+)$/);
    if (h2Match) {
      // Save previous section (skip empty intro)
      const contentStr = currentContent.join('\n').trim();
      if (contentStr) {
        sections.push({ title: currentTitle, content: contentStr });
        isFirstSection = false;
      } else if (!isFirstSection) {
        isFirstSection = false;
      }
      currentTitle = h2Match[1].trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }

  // Don't forget the last section
  const lastContent = currentContent.join('\n').trim();
  if (lastContent || sections.length === 0) {
    sections.push({ title: currentTitle, content: lastContent });
  }

  // If single section with no ## in content, use "Full Article" title
  if (sections.length === 1 && !content.includes('## ')) {
    return [{ title: 'Full Article', content: content.trim() }];
  }

  // Group into 3-4 broader sections for easier navigation
  return groupIntoSections(sections, 4);
}

/** Merges many sections into ~targetCount (3-4) larger parts. */
function groupIntoSections(sections: BlogSection[], targetCount: number = 4): BlogSection[] {
  if (sections.length <= targetCount) return sections;
  const result: BlogSection[] = [];
  const chunkSize = Math.ceil(sections.length / targetCount);
  for (let i = 0; i < sections.length; i += chunkSize) {
    const chunk = sections.slice(i, i + chunkSize);
    const title = chunk[0].title;
    const content = chunk.map((s) => `## ${s.title}\n\n${s.content}`).join('\n\n');
    result.push({ title, content });
  }
  return result;
}
